/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";

// Material Kit 2 React components
import MKBox from "../../components/MKBox";
import MKTypography from "../../components/MKTypography";

// Material Kit 2 React examples
import DefaultNavbar from "../../examples/Navbars/DefaultNavbar";
import DefaultFooter from "../../examples/Footers/DefaultFooter";

// Routes
import routeKeys from "../../routeKeys";

// i18n
import { useLanguage } from "../../i18n";

import { legalbar } from "../EmtayPage/sections/legalbar";

// Images
import bgImage from "../../assets/images/emtay/bg-emtay.webp";

// Styles
import styles from "assets/scss/emtay.module.scss";

/* eslint-disable react/prop-types, no-unused-vars */
function ServiceSection({ title, description, keypoints, reversed, icon }) {
  // Different icons for each service's keypoints
  const keypointIcons =
    icon === "code"
      ? ["code", "web", "integration_instructions"]
      : icon === "settings_overscan"
      ? ["assessment", "trending_up", "check_circle"]
      : ["psychology", "group", "verified"];

  return (
    <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
      <Grid item xs={12} lg={5} order={{ xs: reversed ? 2 : 1, lg: reversed ? 2 : 1 }}>
        <MKTypography variant="h3" my={2}>
          {title}
        </MKTypography>
        <MKTypography variant="body2" color="text" mb={3}>
          {description}
        </MKTypography>
      </Grid>
      <Grid item xs={12} lg={1} />
      <Grid
        item
        xs={12}
        lg={5}
        order={{ xs: reversed ? 1 : 2, lg: reversed ? 1 : 2 }}
        sx={{ ml: { xs: -2, lg: "auto" } }}
      >
        <Stack className={styles.servicesBlock}>
          {keypoints.map((keypoint, index) => (
            <MKBox display="flex" alignItems="center" p={2} key={index}>
              <MKBox
                width="3rem"
                height="3rem"
                variant="gradient"
                bgColor="info"
                color="white"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="xl"
                className={styles.iconBox}
                mr={2}
              >
                <Icon fontSize="small">{keypointIcons[index] || "check"}</Icon>
              </MKBox>
              <MKTypography variant="body2" color="text">
                {keypoint}
              </MKTypography>
            </MKBox>
          ))}
        </Stack>
      </Grid>
    </Grid>
  );
}

function ServicesPage() {
  const { t } = useLanguage();

  // Translate routes
  const routes = routeKeys.map((route) => ({
    ...route,
    name: t(route.key),
  }));

  const services = [
    {
      id: "software-development",
      title: t("services.service1Title"),
      description: t("services.service1Desc"),
      keypoints: t("services.service1Keypoints"),
      icon: "code",
      reversed: false,
    },
    {
      id: "project-audits",
      title: t("services.service2Title"),
      description: t("services.service2Desc"),
      keypoints: t("services.service2Keypoints"),
      icon: "settings_overscan",
      reversed: true,
    },
    {
      id: "technical-screening",
      title: t("services.service3Title"),
      description: t("services.service3Desc"),
      keypoints: t("services.service3Keypoints"),
      icon: "interpreter_mode",
      reversed: false,
    },
  ];

  const manifestoBulletPoints = [
    { text: t("manifesto.bullet1"), icon: "person" },
    { text: t("manifesto.bullet2"), icon: "visibility" },
    { text: t("manifesto.bullet3"), icon: "shield" },
    { text: t("manifesto.bullet4"), icon: "sync" },
  ];

  return (
    <>
      <a name="top" />
      <DefaultNavbar routes={routes} transparent light />
      <MKBox
        width="100%"
        sx={{
          minHeight: { xs: "26rem", md: "22rem" },
          height: { xs: "48vh", md: "40vh" },
          maxHeight: { xs: "38rem", md: "34rem" },
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{
              mx: "auto",
              textAlign: "center",
              pt: { xs: 10, sm: 11, md: 12, lg: 0 },
            }}
          >
            <MKTypography variant="h2" color="white">
              {t("services.title")}
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              {t("services.subtitle")}
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 2,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Container sx={{ py: 4 }}>
          <MKTypography
            variant="body2"
            color="text"
            mb={6}
            sx={{ maxWidth: 800, mx: "auto", textAlign: "center" }}
          >
            {t("services.intro")}
          </MKTypography>

          {services.map((service) => (
            <a name={`services-${service.id}`} key={service.id}>
              <ServiceSection
                title={service.title}
                description={service.description}
                keypoints={service.keypoints}
                icon={service.icon}
                reversed={service.reversed}
              />
            </a>
          ))}

          {/* Agentic Engineering Manifesto */}
          <MKBox
            component="section"
            py={6}
            sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, mt: 4 }}
          >
            <Grid container alignItems="center">
              <Grid item xs={12} lg={5}>
                <MKTypography variant="h3" my={1}>
                  {t("manifesto.title")}
                </MKTypography>
                <MKTypography variant="body2" color="text" mt={2} mb={4}>
                  {t("manifesto.description")}
                </MKTypography>
              </Grid>
              <Grid item xs={12} lg={6} sx={{ ml: { xs: -2, lg: "auto" }, mt: { xs: 6, lg: 0 } }}>
                <Stack className={styles.servicesBlock}>
                  {manifestoBulletPoints.map((bullet, index) => (
                    <MKBox display="flex" alignItems="center" p={2} key={index}>
                      <MKBox
                        width="3rem"
                        height="3rem"
                        variant="gradient"
                        bgColor="info"
                        color="white"
                        coloredShadow="info"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="xl"
                        className={styles.iconBox}
                        mr={2}
                      >
                        <Icon fontSize="small">{bullet.icon}</Icon>
                      </MKBox>
                      <MKTypography variant="body2" color="text">
                        {bullet.text}
                      </MKTypography>
                    </MKBox>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </MKBox>
        </Container>
      </Card>
      <MKBox pt={2} px={1} mt={0} py={0}>
        <DefaultFooter content={{ copyright: legalbar }} />
      </MKBox>
    </>
  );
}

export default ServicesPage;
